/**
 * useNotification — composable wrapper around the toast notification system.
 *
 * Calls `showToast` from `@/plugins/toast` directly (no Vuex round-trip).
 *
 * Usage:
 *   const { notify, notifySuccess, notifyError } = useNotification()
 *   notifySuccess(t('messages.profile_updated'))
 *   notifyError(t('messages.server_error'))
 *   notify({ text: 'Custom', tone: 'warning', durationMs: 5000 })
 *
 * Outside component setup (store actions, utility functions), import
 * `showToast` directly from `@/plugins/toast`.
 */

import { showToast } from '@/plugins/toast'

/**
 * @typedef {Object} NotificationPayload
 * @property {string} text          Message to display
 * @property {'success'|'danger'|'error'|'warning'|'info'} [tone='info']  Visual style
 * @property {number|false} [durationMs=3200]  Auto-close duration, or false to persist
 */

export function useNotification() {
  /**
   * Show a toast notification with full control over options.
   * @param {string|NotificationPayload} payload
   */
  function notify(payload) {
    showToast(payload)
  }

  /**
   * Show a success toast.
   * @param {string} text
   * @param {number} [durationMs=3200]
   */
  function notifySuccess(text, durationMs = 3200) {
    notify({ text, tone: 'success', durationMs })
  }

  /**
   * Show an error toast.
   * @param {string} text
   * @param {number} [durationMs=4000]
   */
  function notifyError(text, durationMs = 4000) {
    notify({ text, tone: 'danger', durationMs })
  }

  /**
   * Show a warning toast.
   * @param {string} text
   * @param {number} [durationMs=3200]
   */
  function notifyWarning(text, durationMs = 3200) {
    notify({ text, tone: 'warning', durationMs })
  }

  /**
   * Show an informational toast.
   * @param {string} text
   * @param {number} [durationMs=3200]
   */
  function notifyInfo(text, durationMs = 3200) {
    notify({ text, tone: 'info', durationMs })
  }

  return {
    notify,
    notifySuccess,
    notifyError,
    notifyWarning,
    notifyInfo,
  }
}
